import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { bookingsAPI, customersAPI, productsAPI, extractErrorMessage } from '../api';

const STATUS_TABS = ['all', 'CONFIRMED', 'ACTIVE', 'RETURNED', 'CANCELLED'];

const Bookings = () => {
  const { selectedStore } = useStore();
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [todayActive, setTodayActive] = useState(0);
  const [dueReturns, setDueReturns] = useState(0);
  const [formData, setFormData] = useState({
    customer_id: '', product_ids: [], rental_start_date: '', rental_end_date: '', notes: '',
  });

  const loadBookings = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeTab !== 'all') params.status = activeTab;
      const response = await bookingsAPI.list(selectedStore.id, params);
      setBookings(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load bookings'));
    } finally {
      setLoading(false);
    }
  }, [selectedStore, activeTab]);

  const loadCustomers = useCallback(async () => {
    try {
      const response = await customersAPI.list(selectedStore.id);
      setCustomers(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) { /* optional */ }
  }, [selectedStore]);

  const loadProducts = useCallback(async () => {
    try {
      const response = await productsAPI.list(selectedStore.id, { rentable_only: true });
      setProducts(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (err) { /* optional */ }
  }, [selectedStore]);

  const loadQuickInfo = useCallback(async () => {
    try {
      const [activeRes, dueRes] = await Promise.allSettled([
        bookingsAPI.getTodayActive(selectedStore.id),
        bookingsAPI.getDueReturns(selectedStore.id),
      ]);
      if (activeRes.status === 'fulfilled') setTodayActive(activeRes.value.data?.count || (Array.isArray(activeRes.value.data) ? activeRes.value.data.length : 0));
      if (dueRes.status === 'fulfilled') setDueReturns(dueRes.value.data?.count || (Array.isArray(dueRes.value.data) ? dueRes.value.data.length : 0));
    } catch (err) { /* optional */ }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedStore) {
      loadBookings();
      loadCustomers();
      loadProducts();
      loadQuickInfo();
    }
  }, [selectedStore, loadBookings, loadCustomers, loadProducts, loadQuickInfo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, store_id: selectedStore.id };
      if (editingId) {
        await bookingsAPI.update(editingId, data);
      } else {
        await bookingsAPI.create(data);
      }
      loadBookings();
      loadQuickInfo();
      resetForm();
    } catch (err) {
      setError(extractErrorMessage(err, 'Operation failed'));
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this booking?')) return;
    try {
      await bookingsAPI.update(id, { status: 'CANCELLED' });
      loadBookings();
      loadQuickInfo();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to cancel booking'));
    }
  };

  const handleSendConfirmation = async (id) => {
    try {
      const response = await bookingsAPI.sendConfirmation(id);
      if (response.data?.whatsapp_url) {
        window.open(response.data.whatsapp_url, '_blank');
      } else {
        alert('Confirmation sent!');
      }
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to send confirmation'));
    }
  };

  const handleProductToggle = (productId) => {
    setFormData(prev => {
      const ids = prev.product_ids.includes(productId)
        ? prev.product_ids.filter(id => id !== productId)
        : [...prev.product_ids, productId];
      return { ...prev, product_ids: ids };
    });
  };

  const resetForm = () => {
    setFormData({ customer_id: '', product_ids: [], rental_start_date: '', rental_end_date: '', notes: '' });
    setEditingId(null);
    setShowForm(false);
    setError(null);
  };

  const getStatusBadge = (status) => {
    const s = (status || '').toUpperCase();
    const map = { CONFIRMED: 'badge-info', ACTIVE: 'badge-success', RETURNED: 'badge-primary', CANCELLED: 'badge-danger', PENDING: 'badge-warning' };
    return map[s] || 'badge-info';
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div><span>Loading bookings...</span></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Bookings</h1>
        <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); if (showForm) resetForm(); }}>
          {showForm ? 'Cancel' : '+ New Booking'}
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="kpi-grid">
        <div className="kpi-card primary">
          <div className="kpi-label">Today Active</div>
          <div className="kpi-value">{todayActive}</div>
        </div>
        <div className="kpi-card warning">
          <div className="kpi-label">Due Returns</div>
          <div className="kpi-value">{dueReturns}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Total Bookings</div>
          <div className="kpi-value">{bookings.length}</div>
        </div>
      </div>

      <div className="tab-bar">
        {STATUS_TABS.map(tab => (
          <button key={tab} className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}>
            {tab === 'all' ? 'All' : tab.charAt(0) + tab.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3 className="card-title">{editingId ? 'Edit Booking' : 'New Booking'}</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Customer *</label>
                  <select className="form-select" value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: parseInt(e.target.value) })} required>
                    <option value="">Select Customer</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name} - {c.whatsapp_phone}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Start Date *</label>
                  <input type="date" className="form-input" value={formData.rental_start_date}
                    onChange={(e) => setFormData({ ...formData, rental_start_date: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date *</label>
                  <input type="date" className="form-input" value={formData.rental_end_date}
                    onChange={(e) => setFormData({ ...formData, rental_end_date: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Products *</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
                  {products.map(p => (
                    <div key={p.id} className="form-checkbox">
                      <input type="checkbox" id={`prod-${p.id}`}
                        checked={formData.product_ids.includes(p.id)}
                        onChange={() => handleProductToggle(p.id)} />
                      <label htmlFor={`prod-${p.id}`}>{p.name}</label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" value={formData.notes} rows={3}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">{editingId ? 'Update Booking' : 'Create Booking'}</button>
                {editingId && <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancel</button>}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mobile Cards View */}
      <div className="mobile-cards hidden-desktop">
        {bookings.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📅</div>
            <h3 className="empty-state-title">No bookings found</h3>
            <p>Create your first booking to get started</p>
          </div>
        ) : (
          bookings.map(booking => (
            <div className="mobile-card" key={booking.id}>
              <div className="mobile-card-header">
                <div>
                  <div className="mobile-card-title">#{booking.booking_number || booking.id}</div>
                  <div className="text-muted" style={{ fontSize: '0.8rem' }}>{booking.customer_name || booking.customer?.name || '-'}</div>
                </div>
                <span className={`badge ${getStatusBadge(booking.status)}`}>{booking.status}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Items</span>
                <span>{booking.items?.length || booking.product_ids?.length || '-'}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Date Range</span>
                <span>{booking.rental_start_date ? `${new Date(booking.rental_start_date).toLocaleDateString()} - ${new Date(booking.rental_end_date).toLocaleDateString()}` : '-'}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Total</span>
                <span className="font-bold">${parseFloat(booking.total_amount || 0).toFixed(2)}</span>
              </div>
              <div className="mobile-card-actions">
                <button className="btn btn-sm btn-secondary" onClick={() => handleSendConfirmation(booking.id)}>WhatsApp</button>
                {booking.status !== 'CANCELLED' && booking.status !== 'RETURNED' && (
                  <button className="btn btn-sm btn-danger" onClick={() => handleCancel(booking.id)}>Cancel</button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="card desktop-table">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Booking #</th><th>Customer</th><th>Items</th><th>Date Range</th><th>Total</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map(booking => (
                <tr key={booking.id}>
                  <td className="font-medium">#{booking.booking_number || booking.id}</td>
                  <td>{booking.customer_name || booking.customer?.name || '-'}</td>
                  <td>{booking.items?.length || booking.product_ids?.length || '-'} items</td>
                  <td>{booking.rental_start_date ? `${new Date(booking.rental_start_date).toLocaleDateString()} - ${new Date(booking.rental_end_date).toLocaleDateString()}` : '-'}</td>
                  <td className="font-bold">${parseFloat(booking.total_amount || 0).toFixed(2)}</td>
                  <td><span className={`badge ${getStatusBadge(booking.status)}`}>{booking.status}</span></td>
                  <td>
                    <div className="flex gap-2">
                      <button className="btn btn-sm btn-secondary" onClick={() => handleSendConfirmation(booking.id)}>WhatsApp</button>
                      {booking.status !== 'CANCELLED' && booking.status !== 'RETURNED' && (
                        <button className="btn btn-sm btn-danger" onClick={() => handleCancel(booking.id)}>Cancel</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {bookings.length === 0 && (
                <tr><td colSpan="7" className="text-center text-muted" style={{ padding: '3rem' }}>No bookings found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Bookings;
